import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import api from '../../config/api';
import paymentService from '../../services/paymentService';

const BankTransfers = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [confirmingOrders, setConfirmingOrders] = useState(new Set());
  const [rejectingOrders, setRejectingOrders] = useState(new Set());
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch bank transfer orders
  const fetchBankTransferOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/orders', {
        params: {
          paymentMethod: 'bank',
          limit: 100
        }
      });
      setOrders(response.data?.data?.orders || []);
    } catch (error) {
      console.error('Error fetching bank transfer orders:', error);
      toast.error('فشل في جلب طلبات التحويل البنكي');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankTransferOrders();
  }, []);

  // Filter orders based on search and status
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.buyer?.lab?.labName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.shippingAddress?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'pending' && order.payment?.status === 'pending_bank_transfer') ||
      (statusFilter === 'uploaded' && order.payment?.status === 'bank_transfer_uploaded') ||
      (statusFilter === 'confirmed' && order.payment?.status === 'paid') ||
      (statusFilter === 'rejected' && order.payment?.status === 'rejected');
    
    return matchesSearch && matchesStatus;
  });

  // Confirm bank transfer
  const handleConfirmTransfer = async (orderId) => {
    // Check if this order is already being processed
    if (confirmingOrders.has(orderId)) {
      toast.error('جاري تأكيد هذا التحويل، يرجى الانتظار...');
      return;
    }

    if (!window.confirm('هل أنت متأكد من تأكيد هذا التحويل البنكي؟')) {
      return;
    }

    // Add to confirming set
    setConfirmingOrders(prev => new Set(prev).add(orderId));
    setProcessing(true);
    
    try {
      await paymentService.confirmBankTransfer(orderId);
      toast.success('تم تأكيد التحويل البنكي بنجاح');
      fetchBankTransferOrders(); // Refresh the list
    } catch (error) {
      console.error('Error confirming bank transfer:', error);
      toast.error(error.message || 'فشل في تأكيد التحويل البنكي');
    } finally {
      // Remove from confirming set
      setConfirmingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
      setProcessing(false);
    }
  };

  // Reject bank transfer
  const handleRejectTransfer = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 5) {
      toast.error('يرجى إدخال سبب الرفض (على الأقل 5 أحرف)');
      return;
    }

    const orderId = selectedOrder._id;
    
    // Check if this order is already being processed
    if (rejectingOrders.has(orderId)) {
      toast.error('جاري رفض هذا التحويل، يرجى الانتظار...');
      return;
    }

    // Add to rejecting set
    setRejectingOrders(prev => new Set(prev).add(orderId));
    
    try {
      await paymentService.rejectBankTransfer(orderId, rejectReason.trim());
      toast.success('تم رفض التحويل البنكي وإرجاع الكميات');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedOrder(null);
      fetchBankTransferOrders(); // Refresh the list
    } catch (error) {
      console.error('Error rejecting bank transfer:', error);
      toast.error(error.message || 'فشل في رفض التحويل البنكي');
    } finally {
      // Remove from rejecting set
      setRejectingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_bank_transfer':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-4 h-4 ml-1" />
            في انتظار التحويل
          </span>
        );
      case 'bank_transfer_uploaded':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <DocumentTextIcon className="w-4 h-4 ml-1" />
            تم رفع الإيصال
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIconSolid className="w-4 h-4 ml-1" />
            تم التأكيد
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XMarkIcon className="w-4 h-4 ml-1" />
            مرفوض
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            غير محدد ({status})
          </span>
        );
    }
  };

  // Get secure file URL with auth token
  const getSecureFileUrl = (orderId) => {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${baseUrl}/api/v1/payments/bank-receipt/${orderId}?token=${token}`;
  };

  // View receipt modal
  const viewReceipt = (order) => {
    setSelectedOrder(order);
    setShowReceiptModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BanknotesIcon className="h-8 w-8 ml-3 text-blue-600" />
                إدارة التحويلات البنكية
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                مراجعة وتأكيد طلبات التحويل البنكي من العملاء
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">إجمالي التحويلات</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredOrders.length}
              </p>
            </div>
            <BanknotesIcon className="h-10 w-10 text-blue-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">بانتظار الرفع</p>
              <p className="text-2xl font-bold text-yellow-600">
                {filteredOrders.filter(o => o.payment?.status === 'pending_bank_transfer').length}
              </p>
            </div>
            <ClockIcon className="h-10 w-10 text-yellow-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">تم رفع الإيصال</p>
              <p className="text-2xl font-bold text-blue-600">
                {filteredOrders.filter(o => o.payment?.status === 'bank_transfer_uploaded').length}
              </p>
            </div>
            <DocumentTextIcon className="h-10 w-10 text-blue-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">تم التأكيد</p>
              <p className="text-2xl font-bold text-green-600">
                {filteredOrders.filter(o => o.payment?.status === 'paid').length}
              </p>
            </div>
            <CheckCircleIcon className="h-10 w-10 text-green-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">مرفوض</p>
              <p className="text-2xl font-bold text-red-600">
                {filteredOrders.filter(o => o.payment?.status === 'rejected').length}
              </p>
            </div>
            <XMarkIcon className="h-10 w-10 text-red-500" />
          </div>
        </motion.div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="البحث برقم الطلب أو اسم المختبر..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">جميع الحالات</option>
              <option value="pending">في انتظار التحويل</option>
              <option value="uploaded">تم رفع الإيصال</option>
              <option value="confirmed">تم التأكيد</option>
              <option value="rejected">مرفوض</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">لا توجد طلبات تحويل بنكي</h3>
            <p className="mt-1 text-sm text-gray-500">لم يتم العثور على أي طلبات تحويل بنكي</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    رقم الطلب
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    المختبر
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    المبلغ
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    تاريخ الطلب
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الحالة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <motion.tr
                    key={order._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {order.orderNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-gray-400 ml-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {order.buyer?.lab?.labName || 'مختبر غير محدد'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.shippingAddress?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CurrencyDollarIcon className="h-5 w-5 text-green-600 ml-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {order.totalAmount?.toLocaleString('ar-SA')} ريال
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <CalendarIcon className="h-4 w-4 ml-1" />
                        {new Date(order.createdAt).toLocaleDateString('ar-SA')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.payment?.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2 space-x-reverse">
                        {/* View Receipt Button */}
                        {order.payment?.bankTransfer?.receiptFile && (
                          <button
                            onClick={() => viewReceipt(order)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="عرض الإيصال"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Confirm Button */}
                        {order.payment?.status === 'bank_transfer_uploaded' && (
                          <button
                            onClick={() => handleConfirmTransfer(order._id)}
                            disabled={confirmingOrders.has(order._id)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={confirmingOrders.has(order._id) ? "جاري التأكيد..." : "تأكيد التحويل"}
                          >
                            {confirmingOrders.has(order._id) ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                            ) : (
                              <CheckCircleIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        
                        {/* Reject Button */}
                        {order.payment?.status === 'bank_transfer_uploaded' && (
                          <button
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowRejectModal(true);
                            }}
                            disabled={rejectingOrders.has(order._id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={rejectingOrders.has(order._id) ? "جاري الرفض..." : "رفض التحويل"}
                          >
                            {rejectingOrders.has(order._id) ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                            ) : (
                              <XMarkIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                إيصال التحويل - طلب {selectedOrder.orderNumber}
              </h3>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Order Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900">تفاصيل الطلب</h4>
                <p><strong>رقم الطلب:</strong> {selectedOrder.orderNumber}</p>
                <p><strong>المبلغ:</strong> {selectedOrder.totalAmount?.toLocaleString('ar-SA')} ريال</p>
                <p><strong>تاريخ الطلب:</strong> {new Date(selectedOrder.createdAt).toLocaleDateString('ar-SA')}</p>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900">بيانات المختبر</h4>
                <p><strong>اسم المختبر:</strong> {selectedOrder.buyer?.lab?.labName || 'مختبر غير محدد'}</p>
                <p><strong>البريد الإلكتروني:</strong> {selectedOrder.shippingAddress?.email}</p>
                <p><strong>الهاتف:</strong> {selectedOrder.shippingAddress?.phone}</p>
                <p><strong>اسم المسؤول:</strong> {selectedOrder.shippingAddress?.fullName}</p>
              </div>
            </div>

            {/* Receipt Image/File */}
            {selectedOrder.payment?.bankTransfer?.receiptFile && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">إيصال التحويل</h4>
                <div className="border border-gray-300 rounded-lg p-4">
                  {selectedOrder.payment.bankTransfer.receiptFile.toLowerCase().includes('.pdf') ? (
                    <div className="text-center py-8">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 mb-4">ملف PDF</p>
                      <a
                        href={getSecureFileUrl(selectedOrder._id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blueSecondary"
                      >
                        عرض الملف
                      </a>
                    </div>
                  ) : (
                    <img
                      src={getSecureFileUrl(selectedOrder._id)}
                      alt="إيصال التحويل"
                      className="max-w-full h-auto rounded-lg"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 space-x-reverse">
              <button
                onClick={() => setShowReceiptModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                إغلاق
              </button>
              {selectedOrder.payment?.status === 'bank_transfer_uploaded' && (
                <>
                  <button
                    onClick={() => {
                      setShowReceiptModal(false);
                      setShowRejectModal(true);
                    }}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    رفض التحويل
                  </button>
                  <button
                    onClick={() => {
                      handleConfirmTransfer(selectedOrder._id);
                      setShowReceiptModal(false);
                    }}
                    disabled={confirmingOrders.has(selectedOrder._id)}
                    className="px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blueSecondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {confirmingOrders.has(selectedOrder._id) ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ml-2"></div>
                        جاري التأكيد...
                      </div>
                    ) : (
                      'تأكيد التحويل'
                    )}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
          >
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <XMarkIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                رفض التحويل البنكي
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                طلب رقم: {selectedOrder.orderNumber}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                سبب الرفض <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="مثال: الإيصال غير واضح أو المبلغ غير صحيح..."
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                سيتم إشعار المختبر بسبب الرفض وإرجاع الكميات المحجوزة
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                  setSelectedOrder(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleRejectTransfer}
                disabled={!rejectReason.trim() || rejectReason.trim().length < 5 || rejectingOrders.has(selectedOrder._id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {rejectingOrders.has(selectedOrder._id) ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ml-2"></div>
                    جاري الرفض...
                  </div>
                ) : (
                  'تأكيد الرفض'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default BankTransfers;
